import { type ComponentPropsWithoutRef, type ReactNode } from 'react'

import { cn } from '@/lib/utils'

/**
 * Shared visual language for every row inside a widget's "..." menu
 * (WidgetActionsMenu's additionalItems), matching the existing hardcoded
 * layout-action rows (Move to Top / Minimize / Duplicate / Delete) so
 * relocated settings/columns/filter/mode controls look identical to them.
 */
export const WIDGET_MENU_ROW_CLASS =
  'flex w-full cursor-pointer items-center gap-2.5 px-3 py-2 text-foreground text-left text-sm tracking-tight transition-colors hover:bg-foreground/10'

interface WidgetMenuRowContentProps {
  icon: ReactNode
  label: ReactNode
  description?: string
  /** e.g. a <SelectValue /> showing the current selection, or a chevron affordance. */
  trailing?: ReactNode
}

/**
 * Non-interactive row visual (icon + label + optional trailing content).
 * Renders no interactive element of its own, so it's safe to place inside
 * ANY trigger element — a plain button, a DropdownMenuTrigger asChild, or a
 * SelectTrigger (which already renders its own <button> and would break if
 * WidgetMenuRow's <button> were nested inside it).
 */
export function WidgetMenuRowContent({
  icon,
  label,
  description,
  trailing,
}: WidgetMenuRowContentProps) {
  if (!description) {
    return (
      <>
        {icon}
        <span className="flex-1 truncate text-left">{label}</span>
        {trailing}
      </>
    )
  }
  return (
    <>
      <span className="text-foreground/60 shrink-0" aria-hidden="true">
        {icon}
      </span>
      <span className="min-w-0 flex-1 text-left">
        <span className="flex items-center gap-1.5">{label}</span>
        {description && (
          <span className="text-foreground/60 mt-0.5 block text-xs leading-relaxed">
            {description}
          </span>
        )}
      </span>
      {trailing}
    </>
  )
}

interface WidgetMenuRowProps extends ComponentPropsWithoutRef<'button'> {
  icon: ReactNode
  label: ReactNode
  description?: string
  trailing?: ReactNode
}

/**
 * Interactive row button for one-shot actions or as a DropdownMenuTrigger's
 * asChild content. Not for use inside SelectTrigger — use WidgetMenuRowContent
 * there instead, since SelectTrigger already renders its own <button>.
 */
export function WidgetMenuRow({
  icon,
  label,
  description,
  trailing,
  className,
  ...props
}: WidgetMenuRowProps) {
  return (
    <button className={cn(WIDGET_MENU_ROW_CLASS, className)} {...props}>
      <WidgetMenuRowContent
        icon={icon}
        label={label}
        description={description}
        trailing={trailing}
      />
    </button>
  )
}
